import { observer } from "mobx-react-lite"
import { useRef } from "react"
import { DataSetContext } from "../../hooks/use-data-set-context"
import { logStringifiedObjectMessage } from "../../lib/log-message"
import { appState } from "../../models/app-state"
import { updateAttributesNotification, updateCasesNotification } from "../../models/data/data-set-notifications"
import { setAttributeFormulaWithCustomUndoRedo } from "../../models/data/data-set-undo"
import { getMetadataFromDataSet, getSharedDataSets } from "../../models/shared/shared-data-utils"
import { uiState } from "../../models/ui-state"
import { t } from "../../utilities/translation/translate"
import { editFormulaNotification } from "./edit-formula-notifications"
import { EditFormulaModal } from "./edit-formula-modal"

export const EditAttributeFormulaModal = observer(function EditAttributeFormulaModal() {
  const attributeId = uiState.editFormulaAttributeId
  const finalFocusRef = useRef<HTMLElement | null>(null)
  // Only update when non-null so the ref survives the close cycle (clearing the
  // UIState element must not nullify the ref before Chakra restores focus).
  // When opening without an explicit focus element (e.g. via DI API), clear any stale ref.
  if (attributeId) {
    if (uiState.editFormulaFinalFocusElement) {
      finalFocusRef.current = uiState.editFormulaFinalFocusElement
    } else if (finalFocusRef.current) {
      finalFocusRef.current = null
    }
  }
  const dataSet = getSharedDataSets(appState.document).find(ds => ds.dataSet.attrFromID(attributeId))?.dataSet
  const attribute = dataSet?.attrFromID(attributeId)
  const value = attribute?.formula?.display

  const applyFormula = (formula: string, attrName?: string) => {
    if (attribute) {
      dataSet?.applyModelChange(() => {
        setAttributeFormulaWithCustomUndoRedo(dataSet, attribute, formula, attrName)
      }, {
        notify: () => {
          const metadata = getMetadataFromDataSet(dataSet)
          const caseTableTile = metadata?.caseTableTileId
            ? appState.document.content?.getTile(metadata.caseTableTileId)
            : undefined
          const notifications = [
            updateCasesNotification(dataSet),
            updateAttributesNotification([attribute], dataSet)
          ]
          const editFormula = editFormulaNotification(caseTableTile)
          if (editFormula) notifications.push(editFormula)
          return notifications
        },
        undoStringKey: "DG.Undo.caseTable.editAttributeFormula",
        redoStringKey: "DG.Redo.caseTable.editAttributeFormula",
        log: logStringifiedObjectMessage("Edit attribute formula: %@",
              {name: attribute.name, collection: dataSet?.getCollectionForAttribute(attributeId)?.name, formula},
              "data")
      })
    }
  }

  return (
    <DataSetContext.Provider value={dataSet}>
      <EditFormulaModal
        applyFormula={applyFormula}
        finalFocusRef={finalFocusRef}
        formulaPrompt={t("DG.AttrFormView.formulaPrompt")}
        isOpen={!!uiState.editFormulaAttributeId}
        onClose={() => uiState.setEditFormulaAttributeId()}
        titleInput={attribute?.name}
        titleLabel={t("DG.AttrFormView.attrNamePrompt")}
        titlePlaceholder={t("V3.AttrFormView.attrPlaceholder")}
        value={value}
      />
    </DataSetContext.Provider>
  )
})
